import { ModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/ModelCascadeRepository';
import { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { ViewCollection } from '@view-model/domain/view/ViewCollection';
import type { StickyModelContents } from '@view-model/domain/model/model-contents/StickyModelContents';
import { ModelContentsCollection } from '@view-model/domain/model/model-contents/ModelContentsCollection';
import { ViewsClipboardPayload } from './ViewsClipboardPayload';
import { PositionSetCreateCommand } from '@view-model/models/common/PositionSet/PositionSetCreateCommand';
import { PositionSet } from '@view-model/models/common/PositionSet';
import type { GroupId, UserId, ViewId, ViewModelId } from 'balus-common';
import type { ModelKey, ViewKey } from '@view-model/domain/key';
import type { Point } from '@view-model/models/common/basic';
import type { CommandManager } from '@view-model/models/framework/command';
import { CompositeCommand, CreateCommand } from '@view-model/models/framework/command';
import type { ViewModelOperationLogSender } from '@view-model/models/framework/action-log';
import { ViewModelAssetCollectionForClone } from '@view-model/domain/view-model';
import { FileContainerAssetCloner } from '@view-model/domain/view-model/FileContainerAssetCloner';
import { FileContainerAssetTrashCommand } from '@view-model/models/sticky/FileContainer/command/FileContainerAssetTrashCommand';

import { ObjectRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { ModelCreateCommand } from '@view-model/command/model/ModelCreateCommand';
import { ViewCollectionContents } from '@view-model/domain/view/ViewCollectionContents';
import { VIEW_GRID_SIZE, viewGridLayout } from '@view-model/ui/components/View/constants';
import { ConsistencyLink } from '@view-model/domain/ConsistencyLink';
import { ConsistencyLinkCollection } from '@view-model/domain/ConsistencyLink/ConsistencyLinkCollection';
import { ClipboardUtils } from '@framework/ClipboardUtils';
import { t } from 'i18next';

/**
 * 貼り付けるビュー群をキャンバス上のどこに配置するかの指定。
 * - center: ビュー群の中心を centerPoint に合わせて全体を平行移動する（クリップボード貼り付け）
 * - absolute: ペイロードが持つ座標をそのまま使う（AIがビュー位置を指定する addView）
 */
type ViewsPastePlacement = { type: 'center'; centerPoint: Point } | { type: 'absolute' };

export class ViewsClipboardOperator {
  static async copy(
    viewModelId: ViewModelId,
    views: ViewCollection,
    viewPositions: PositionSet,
    consistencyLinks: ConsistencyLinkCollection | null,
    logSender: ViewModelOperationLogSender
  ): Promise<boolean> {
    const modelCascadingRepository = new ModelCascadeRepository(viewModelId);

    const res = await Promise.all(views.map((v) => modelCascadingRepository.load(v.modelId)));
    const modelContents = res.filter((r): r is StickyModelContents => !!r);

    const validConsistencyLinks = consistencyLinks
      ? consistencyLinks.validLinksBy(views)
      : ConsistencyLinkCollection.buildEmpty();

    const payload = ViewsClipboardPayload.fromViewsAndModelContents({
      sourceViewModelId: viewModelId,
      views,
      viewPositions,
      modelContents: new ModelContentsCollection(modelContents),
      consistencyLinks: validConsistencyLinks,
    });

    try {
      await ClipboardUtils.copyWithCustomFormat({
        json: JSON.stringify(payload),
      });

      // 複数ビューコピーの時は、１つ目のビューのみログに残す
      // https://www.notion.so/levii/2674861813664ef299a2e8df8b2023db
      const view = views.map((v) => v)[0];
      logSender('view:copy', {
        clipboardPayloadId: payload.id,
        viewId: view.id,
        viewName: view.name.value,
      });

      return true;
    } catch (e) {
      console.warn('クリップボードへのコピーに失敗しました', e);
      return false;
    }
  }

  static async paste(
    payload: ViewsClipboardPayload,
    placement: ViewsPastePlacement,
    groupId: GroupId,
    viewModelId: ViewModelId,
    currentUserId: UserId,
    commandManager: CommandManager,
    logSender: ViewModelOperationLogSender,
    // regenerateIds: false の場合、ID の再採番(cloneNew)を行わず、ペイロードのIDをそのまま永続化する。
    // AIアシスタント経由の addView で、採番済みの実IDを差分編集(patchView)時に再利用させるために使う。
    options?: { regenerateIds?: boolean }
  ): Promise<[Set<ViewId>, string | null]> {
    const originalModelContentsCollection = ModelContentsCollection.load(payload.modelContents);
    const modelCascadingRepository = new ModelCascadeRepository(viewModelId);

    let newModelContentsCollection: ModelContentsCollection;
    let newModelContentsKeyMap: Record<string, ModelKey>;
    let newViews: ViewCollection;
    let newViewKeyMap: Record<string, ViewKey>;
    let newViewIdMap: Record<string, string>;
    let errorMessage: string | null = null;

    if (options?.regenerateIds === false) {
      // ID を再採番しない。ペイロードのモデル/ビューをそのまま使い、各マップは恒等写像にする。
      newModelContentsCollection = originalModelContentsCollection;
      newModelContentsKeyMap = Object.fromEntries(
        originalModelContentsCollection.list().map((m) => [m.key.toString(), m.key])
      );
      newViews = ViewCollection.load(payload.views);
      newViewKeyMap = Object.fromEntries(newViews.map((v) => [v.key.toString(), v.key]));
      newViewIdMap = Object.fromEntries(newViews.map((v) => [v.id, v.id]));
    } else {
      const fileAssetClone = await FileContainerAssetCloner.clone(
        payload.sourceViewModelId,
        { groupId, viewModelId },
        originalModelContentsCollection.fileContainerAssetIds(),
        currentUserId
      );
      if (!fileAssetClone.ok) return [new Set(), fileAssetClone.errorMessage];

      // アセットの複製
      // 復元したペイロード中に含まれる ViewModelAssets の情報を取得して複製する
      const assets = await ViewModelAssetCollectionForClone.fetchByUrlList(originalModelContentsCollection.assetUrls());
      const baseUrl = location.href;
      const [newAssetUrlMap, hasError] = await assets.cloneNew(groupId, viewModelId, currentUserId, baseUrl);
      errorMessage = hasError ? t('canvas.asset.cloneFailed') : null;

      // モデルの複製
      [newModelContentsCollection, newModelContentsKeyMap] = originalModelContentsCollection.cloneNew(
        newAssetUrlMap,
        fileAssetClone.assetCopy
      );

      // ビューの複製
      [newViews, newViewKeyMap, newViewIdMap] = ViewCollection.load(payload.views).cloneNew(newModelContentsKeyMap);
    }

    // ビューポジションの複製と移動
    const originalViewPositions = PositionSet.load(payload.viewPositions);
    const clonedViewPositions = originalViewPositions.cloneNew(newViewIdMap);
    const clonedViewsRect = new ViewCollectionContents(newViews, clonedViewPositions).getUnionRect();
    if (!clonedViewsRect) return [new Set(), t('canvas.clipboard.noViewsToPaste')];

    const newViewPositions = ((): PositionSet => {
      // 配置先が指定済みなので平行移動しない。グリッドへのスナップだけを行う
      if (placement.type === 'absolute') return clonedViewPositions.snapToLayout(viewGridLayout);

      const originalViewRectCenterPoint = clonedViewsRect.getCenterPoint();
      const subtraction = placement.centerPoint.subtract(originalViewRectCenterPoint);
      const movedViewPositions = clonedViewPositions.moveAll(subtraction.x, subtraction.y).snapToLayout(viewGridLayout);

      // 貼り付けるビューの位置が全て元のビューの一と同じ場合は
      // ペーストしたビューが元のビューに完全に被ってしまうのでグリッド１つ分だけずらす
      return originalViewPositions
        .all()
        .every(
          ([originalViewId, p]) =>
            newViewIdMap[originalViewId] && movedViewPositions.find(newViewIdMap[originalViewId])?.isEqual(p)
        )
        ? movedViewPositions.moveAll(VIEW_GRID_SIZE, VIEW_GRID_SIZE).snapToLayout(viewGridLayout)
        : movedViewPositions;
    })();

    // 整合性リンクの複製
    const newConsistencyLinks = ConsistencyLinkCollection.load(payload.consistencyLinks).cloneNew(newViewKeyMap);

    const commands = newViews
      .map((newView) => {
        const newModelContents = newModelContentsCollection.list().find((c) => c.key.isEqual(newView.modelKey));
        if (!newModelContents) return null;

        const repository = new ObjectRepository(ViewEntity, RTDBPath.View.viewPath(viewModelId, newView.id));
        const viewCreateCommand = new CreateCommand(newView, repository);
        const modelCreateCommand = new ModelCreateCommand(modelCascadingRepository, newModelContents);

        return new CompositeCommand(modelCreateCommand, viewCreateCommand);
      })
      .filter((command): command is CompositeCommand => !!command);

    const newViewsPositionSetCreateCommand = new PositionSetCreateCommand(
      newViewPositions,
      RTDBPath.View.positionsPath(viewModelId)
    );

    const newConsistencyLinksCreateCommands = newConsistencyLinks.map((consistencyLink) => {
      const repository = new ObjectRepository(
        ConsistencyLink,
        RTDBPath.ConsistencyLink.linkPath(viewModelId, consistencyLink.id)
      );
      return new CreateCommand(consistencyLink, repository);
    });

    FileContainerAssetTrashCommand.executeCreation(
      commandManager,
      { groupId, viewModelId },
      newModelContentsCollection.fileContainerAssetIds(),
      new CompositeCommand(...commands, newViewsPositionSetCreateCommand, ...newConsistencyLinksCreateCommands)
    );

    // 複数ビューのペーストの時は、１つ目のビューのみログに残す
    // https://www.notion.so/levii/2674861813664ef299a2e8df8b2023db
    const newView = newViews.map((v) => v)[0];
    logSender('view:paste', {
      clipboardPayloadId: payload.id,
      viewId: newView.id,
      viewName: newView.name.value,
    });

    return [new Set(Object.values(newViews.map((v) => v.id))), errorMessage];
  }
}
